import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';
import { ComfortApiService } from './comfort-api.service';
import { EnergyRatingUKApiService } from './energy-rating-uk-api.service';
import { AirSimulationApiService } from './air-simulation-api.service';
import { EnergySavingsApiService, WinterCalcRequest, SummerCalcRequest } from './energy-savings-api.service';
import { WindowPayloadBuilderService } from './window-payload-builder.service';
import { ThermalApiService } from './thermal-api.service';
import { setDefaultDimensions } from '../utils/calculation';
import { environment } from 'src/environments/environment';
import * as Constants from '../utils/Constants';
import { BehaviorSubject } from 'rxjs';
import { LoadingController } from '@ionic/angular';

export type ConfigChangeType = 'windowtype' | 'frame' | 'glass' | 'spacer' | 'muntin';

@Injectable({
  providedIn: 'root',
})
export class CalculationAutoUpdateService {
  private loadingEl: HTMLIonLoadingElement | null = null;

  constructor(
    private loadingController: LoadingController,
    private storageService: StorageService,
    private comfortApi: ComfortApiService,
    private energyRatingApi: EnergyRatingUKApiService,
    private energySavingsApi: EnergySavingsApiService,
    private airSimApi: AirSimulationApiService,
    private windowPayloadBuilder: WindowPayloadBuilderService,
    private thermalApi: ThermalApiService
  ) { }

  async triggerAutoUpdate(
    changeType: ConfigChangeType,
    isComparisonMode: boolean = false
  ): Promise<void> {
    await this.showLoader();
    try {
      const res = isComparisonMode
        ? await this.storageService.getComparedProject()
        : await this.storageService.getProject();
      const project = res ? JSON.parse(res) : null;
      if (!project) return;

      const updates: any = {};

      // --- Comfort ---
      // Guard: surfaceTemperature means user has previously clicked validate (full result saved)
      if (project.comfortCalculator?.surfaceTemperature !== undefined) {
        const result = await this.recalcComfort(project);
        if (result) updates.comfortCalculator = result;
      }

      // --- Energy Rating ---
      // Guard: country means user has previously clicked next (full result saved)
      if (project.windowEnergyRatingResult?.country) {
        const country = project.windowEnergyRatingResult?.country;
        const skipForWindowTypeChange = ['Schweiz', 'Frankreich', 'Großbritannien', 'Other'];
        const shouldSkip = changeType === 'windowtype' && skipForWindowTypeChange.includes(country);
        if (!shouldSkip) {
          const result = await this.recalcEnergyRating(project);
          if (result) updates.windowEnergyRatingResult = result;
        }
      }

      // --- Energy Savings ---
      // Guard: season means user has previously clicked validate (full result saved)
      if (project.energySavingsResult?.season && project.energyBasicData) {
        const result = await this.recalcEnergySavings(project);
        if (result) updates.energySavingsResult = result;
      }

      // --- SWS Air Simulation: glass change ONLY ---
      // Guard: shortEdge means user has previously clicked next (values saved)
      if (changeType === 'glass' && project.airSimulationResult?.shortEdge !== undefined) {
        const result = await this.recalcAirSimulation(project);
        if (result) updates.airSimulationResult = result;
      }

      if (Object.keys(updates).length > 0) {
        if (isComparisonMode) {
          await this.storageService.updateComparedProject(updates);
        } else {
          await this.storageService.updateProject(updates);
        }
        console.log('[AutoUpdate] Updated for change:', changeType, Object.keys(updates));
      }
    } catch (err) {
      console.error('[AutoUpdate] Error:', err);
    } finally {
      await this.hideLoader();
    }
  }

  // ── Comfort ────────────────────────────────────────────────────────────────
  private async recalcComfort(project: any): Promise<any | null> {
    try {
      const saved = project.comfortCalculator;
      const payloads = await this.windowPayloadBuilder.buildWindowPayloads();
      const apiParam = {
        windowConfiguration: payloads?.window1,
        te: saved.te,
        ti: saved.ti,
        phi: saved.phi
      };
      const response: any = await this.comfortApi.calculateComfort(apiParam).toPromise();
      if (!response) return null;
      return {
        ...saved,
        surfaceTemperature: response.surfaceTemperature,
        dewPoint: response.dewPoint,
        moldTemperature: response.moldTemperature
      };
    } catch (err) {
      console.error('[AutoUpdate] Comfort failed:', err);
      return null;
    }
  }

  // ── Energy Rating ─────────────────────────────────────────────────────────
  private async recalcEnergyRating(project: any): Promise<any | null> {
    try {
      const saved = project.windowEnergyRatingResult;
      const country = saved.country;
      const endpoints = environment.caluwin_api.endpoints;
      let endpointUrl = '';
      let apiParam: any = {};

      if (country === 'Schweiz') {
        endpointUrl = endpoints.energy_rating_ch;
        const customData = {
          dimensions: { dimA: 1.55, dimB: 1.15, dimC: 0 },
          windowtype: { id: 2 }
        };
        apiParam = (await this.windowPayloadBuilder.buildWindowPayloads(customData))?.window1;

      } else if (country === 'Dänemark') {
        endpointUrl = endpoints.energy_rating_dk;
        apiParam = (await this.windowPayloadBuilder.buildWindowPayloads())?.window1;

      } else if (country === 'Großbritannien') {
        endpointUrl = endpoints.energy_rating_uk;
        const ukSaved = saved.ukResult;
        const windowType = 3;
        const dimensions: any = setDefaultDimensions(windowType);
        const customData = { dimensions, windowtype: { id: windowType } };
        const computedAg = this.calcWerGlassArea(ukSaved?.agWithoutGasket ?? 0, project);
        apiParam = {
          windowConfiguration: (await this.windowPayloadBuilder.buildWindowPayloads(customData))?.window1,
          agWithoutGasket: computedAg,
          airleakage: ukSaved?.airleakage ?? 0.1,
          ool: ukSaved?.ool ?? 4.19
        };

      } else if (country === 'Frankreich') {
        endpointUrl = endpoints.energy_rating_fr;
        const frWindowType = saved.frResult?.selectedFrWindowType ?? '13';
        const dimensions: any = setDefaultDimensions(Number(frWindowType));
        const customData = { dimensions, windowtype: { id: frWindowType } };
        apiParam = {
          windowConfiguration: (await this.windowPayloadBuilder.buildWindowPayloads(customData))?.window1,
          af: 0.4
        };

      } else if (country === 'Other') {
        endpointUrl = endpoints.energy_rating_phe;
        const pheWindowType = saved.pheResult?.selectedRatingWindowType ?? '1';
        const dimensions: any = setDefaultDimensions(Number(pheWindowType));
        const customData = { dimensions, windowtype: { id: pheWindowType } };
        apiParam = {
          windowConfiguration: (await this.windowPayloadBuilder.buildWindowPayloads(customData))?.window1,
          climateZone: saved.pheResult?.climateZone ?? '3'
        };

      } else {
        return null;
      }

      const response: any = await this.energyRatingApi
        .calculateEnergyRating(apiParam, endpointUrl).toPromise();
      if (!response) return null;

      return await this.mergeEnergyRatingResult(saved, country, response, project);
    } catch (err) {
      console.error('[AutoUpdate] Energy Rating failed:', err);
      return null;
    }
  }

  private async mergeEnergyRatingResult(
    saved: any,
    country: string,
    response: any,
    project: any
  ): Promise<any> {
    const updated = { ...saved };

    switch (country) {
      case 'Schweiz': {
        const customData = {
          dimensions: { dimA: 1.55, dimB: 1.15, dimC: 0 },
          windowtype: { id: 2 }
        };
        const payload = (await this.windowPayloadBuilder.buildWindowPayloads(customData))?.window1;
        const ag = payload.glassArea;
        const af = payload.frameArea;
        const aw = payload.windowArea;
        const gValue = project.windowglas?.gValue ?? 0;
        const hs = ag * (gValue / 100) * 2.0;
        const uw = await new Promise<number>((resolve, reject) => {
          this.thermalApi.calculateUw(customData).subscribe({
            next: (res: any) => resolve(res.value),
            error: (err: any) => reject(err)
          });
        });
        const hw = uw * aw;
        updated.chResult = {
          uweq: response.uweq ?? 0,
          rating: response.rating ?? '',
          a: 1.55,
          b: 1.15,
          ag,
          af,
          aw,
          uw,
          hw,
          hs
        };
        break;
      }

      case 'Dänemark':
        updated.dkResult = {
          gg: response.gg ?? 0,
          gw: response.gw ?? 0,
          eref: response.eref ?? 0,
          werclass: response.werClass ?? '',
          areaWindow: response.areaWindow ?? 0,
          areaGlass: response.areaGlass ?? 0,
          uw: response.uw ?? 0
        };
        break;

      case 'Großbritannien':
        updated.ukResult = {
          rat: response.rat ?? '',
          wer: response.wer ?? 0,
          gw: response.gw ?? 0,
          lfac: response.lfac ?? 0,
          agWithoutGasket: saved.ukResult?.agWithoutGasket ?? 0,
          airleakage: saved.ukResult?.airleakage ?? 0.1,
          ool: saved.ukResult?.ool ?? 4.19
        };
        break;

      case 'Frankreich':
        updated.frResult = {
          selectedFrWindowType: saved.frResult?.selectedFrWindowType ?? '13',
          badge_z1: response.badgeZ1 ?? '',
          value_z1: response.valueZ1 ?? 0,
          badge_z2: response.badgeZ2 ?? '',
          value_z2: response.valueZ2 ?? 0,
          badge_z3: response.badgeZ3 ?? '',
          value_z3: response.valueZ3 ?? 0,
          badge_confort_z1: response.badgeConfortZ1 ?? '',
          value_confort_z1: response.valueConfortZ1 ?? 0,
          badge_confort_z2: response.badgeConfortZ2 ?? '',
          value_confort_z2: response.valueConfortZ2 ?? 0,
          badge_confort_z3: response.badgeConfortZ3 ?? '',
          value_confort_z3: response.valueConfortZ3 ?? 0,
          tlw: response.tLw ?? 0,
          sw: response.sw ?? 0
        };
        break;

      case 'Other':
        updated.pheResult = {
          selectedRatingWindowType: saved.pheResult?.selectedRatingWindowType ?? '1',
          psig: response.psig ?? '',
          ug: response.uw ?? 0,
          psiOpaque: response.psiOpaque ?? 0,
          uwPhi: response.uwPhi ?? 0,
          effClass: response.effClass ?? '',
          climateZone: saved.pheResult?.climateZone ?? '3'
        };
        break;
    }

    return updated;
  }

  // Required for Großbritannien: agWithoutGasket sent to API must be the computed glass area
  private calcWerGlassArea(gasket: number, project: any): number {
    const wf = project.windowframe;
    const wValues = wf?.wValues ?? [];
    const fj = wValues[0] ?? 0;
    const fh = wValues[1] ?? 0;
    const fs = wValues[2] ?? 0;
    const sj = wValues[3] ?? 0;
    const sh = wValues[4] ?? 0;
    const ss = wValues[5] ?? 0;
    const m = wValues[6] ?? 0;
    const a = project.dimensions?.a ?? 0;
    const b = project.dimensions?.b ?? 0;
    gasket = gasket / 1000;
    const lw = (a / 2) - (m / 2) - fj - (2 * gasket);
    const lh = b - fh - fs - (2 * gasket);
    const rw = (a / 2) - (m / 2) - sj - (2 * gasket);
    const rh = b - sh - ss - (2 * gasket);
    return (lw * lh) + (rw * rh);
  }

  // ── Energy Savings ────────────────────────────────────────────────────────
  private async recalcEnergySavings(project: any): Promise<any | null> {
    try {
      const saved = project.energySavingsResult;
      const bd = project.energyBasicData;
      const season = saved?.season;
      const eQty = saved?.windowsCount ?? 1;
      const eYrs = saved?.yearsCount ?? 1;
      const projectName = project.projectName ?? 'Default Project';

      const { window1, window2 } = await this.windowPayloadBuilder.buildWindowPayloads();

      let winterResults: any = null;
      let summerResults: any = null;
      let bothResults: any = null;

      if (season === 'winter' && bd?.winter) {
        const payload: WinterCalcRequest = {
          project: projectName,
          externalTemp: bd.winter.externalTemp,
          internalTemp: bd.winter.internalTemp,
          cost: bd.winter.heatingCost,
          currency: bd.winter.heatingCurrency,
          efficiency: bd.winter.efficiency,
          costDelta: bd.winter.costDelta,
          window1, window2,
          windowsCount: eQty,
          yearsCount: eYrs,
          heatSeason: bd.winter.heatSeason,
          heatingFuels: bd.winter.fuelType,
          heatingUnit: bd.winter.heatingUnit,
          calorific: bd.winter.calorificValue
        };
        winterResults = await this.energySavingsApi.calculateWinter(payload).toPromise() ?? null;

      } else if (season === 'summer' && bd?.summer) {
        const payload: SummerCalcRequest = {
          project: projectName,
          externalTemp: bd.summer.externalTemp,
          internalTemp: bd.summer.internalTemp,
          cost: bd.summer.electricalCost,
          currency: bd.summer.electricalCurrency,
          efficiency: bd.summer.efficiency,
          costDelta: bd.summer.costDelta,
          window1, window2,
          windowsCount: eQty,
          yearsCount: eYrs,
          coolSeason: bd.summer.coolSeason,
          power: bd.summer.powerType
        };
        summerResults = await this.energySavingsApi.calculateSummer(payload).toPromise() ?? null;

      } else if (season === 'both' && bd?.winter && bd?.summer) {
        const winterData: WinterCalcRequest = {
          project: projectName,
          externalTemp: bd.winter.externalTemp,
          internalTemp: bd.winter.internalTemp,
          cost: bd.winter.heatingCost,
          currency: bd.winter.heatingCurrency,
          efficiency: bd.winter.efficiency,
          costDelta: bd.winter.costDelta,
          window1, window2,
          windowsCount: eQty,
          yearsCount: eYrs,
          heatSeason: bd.winter.heatSeason,
          heatingFuels: bd.winter.fuelType,
          heatingUnit: bd.winter.heatingUnit,
          calorific: bd.winter.calorificValue
        };
        const summerData: SummerCalcRequest = {
          project: projectName,
          externalTemp: bd.summer.externalTemp,
          internalTemp: bd.summer.internalTemp,
          cost: bd.summer.electricalCost,
          currency: bd.summer.electricalCurrency,
          efficiency: bd.summer.efficiency,
          costDelta: bd.summer.costDelta,
          window1, window2,
          windowsCount: eQty,
          yearsCount: eYrs,
          coolSeason: bd.summer.coolSeason,
          power: bd.summer.powerType
        };
        bothResults = await this.energySavingsApi
          .calculateWinterSummer({ winterData, summerData, window1, window2 })
          .toPromise() ?? null;
        if (bothResults) {
          summerResults = bothResults?.summer;
          winterResults = bothResults?.winter;
        }
      }

      return {
        ...saved,
        summerResult: summerResults ? {
          diffCoolRequirements: summerResults.diffCoolRequirements,
          diffCoolCosts: summerResults.diffCoolCosts,
          diffCO2: summerResults.diffCO2
        } : saved.summerResult,
        wiinterRsult: winterResults ? {
          diffCoolRequirements: winterResults.diffHeatRequirements,
          diffCoolCosts: winterResults.diffHeatCosts,
          diffCO2: winterResults.diffCO2
        } : saved.wiinterRsult,
        summerWinterResult: bothResults ? {
          diffHeatCoolRequirements: bothResults.diffHeatCoolRequirements,
          diffCosts: bothResults.diffCosts,
          diffCO2: bothResults.diffCO2
        } : saved.summerWinterResult
      };
    } catch (err) {
      console.error('[AutoUpdate] Energy Savings failed:', err);
      return null;
    }
  }

  // ── SWS Air Simulation ────────────────────────────────────────────────────
  private async recalcAirSimulation(project: any): Promise<any | null> {
    try {
      const saved = project.airSimulationResult;
      // Only recalc if location or transport data exists (mirrors hasLocationOrTransport guard)
      const hasLocation =
        project.lifetime?.latitude != null &&
        project.lifetime?.longitude != null;
      const hasTransport =
        project.elevatedTransport?.transport &&
        project.elevatedTransport.transport !== '-';
      if (!hasLocation && !hasTransport) return null;

      const result: any = await this.airSimApi
        .calculateAirSimulation(saved.shortEdge, saved.longEdge)
        .toPromise();
      if (!result) return null;

      return {
        ...saved,
        lifetimeResult: Constants.getRatingLifetimeTextKey(result.lifetimeOk, false),
        transportResult: Constants.getRatingElevatedTransportTextKey(result.transportOk)
      };
    } catch (err) {
      console.error('[AutoUpdate] Air Sim failed:', err);
      return null;
    }
  }

  private async showLoader(): Promise<void> {
    try {
      this.loadingEl = await this.loadingController.create({
        message: 'Recalculating...',
        spinner: 'crescent',
        backdropDismiss: false,
        cssClass: 'auto-update-loader'
      });
      await this.loadingEl.present();
    } catch (err) {
      console.error('[AutoUpdate] Loader show failed:', err);
    }
  }

  private async hideLoader(): Promise<void> {
    try {
      if (this.loadingEl) {
        await this.loadingEl.dismiss();
        this.loadingEl = null;
      }
    } catch (err) {
      console.error('[AutoUpdate] Loader hide failed:', err);
    }
  }

}